import { on } from "../utils/listeners";
import { updateThickPriceOnVariantChange } from "./productPage";

const QTY_PICKERS =
  ".js-large-quantity-picker-mobile, .js-large-quantity-picker-desktop";

const setPriceLabels = (
  actualSelector,
  baseSelector,
  actualPrice,
  basePrice
) => {
  const actualPrices = document.querySelectorAll(actualSelector);
  const basePrices = document.querySelectorAll(baseSelector);

  actualPrices.forEach(el => {
    el.innerHTML = actualPrice;
  });

  if (actualPrice === basePrice) {
    basePrices.forEach(el => {
      el.style.display = "none";
    });
  } else {
    basePrices.forEach(el => {
      el.innerHTML = basePrice;
      el.hidden = false;
      el.style.display = "";
    });
  }
};

// The members price labels only exist when a members promotion applies to the listing; the
// option then carries data-members-actual-price / data-members-base-price as well.
const changePriceLabel = ({
  actualPrice,
  basePrice,
  membersActualPrice,
  membersBasePrice
}) => {
  setPriceLabels(".js-actual-price", ".js-base-price", actualPrice, basePrice);

  if (membersActualPrice) {
    setPriceLabels(
      ".js-members-actual-price",
      ".js-members-base-price",
      membersActualPrice,
      membersBasePrice
    );
  }
};

const changeKlarnaPrice = detail => {
  const qtyChangedEvent = new CustomEvent("qtyChanged", {
    detail
  });
  window.dispatchEvent(qtyChangedEvent);
};

const changeCustomiseUrl = ({ qty }) => {
  const customiseButtons = document.querySelectorAll(
    ".js-listing-customise-cta"
  );

  customiseButtons.forEach(customiseButton => {
    const customiseUrl = new URL(customiseButton.href);
    customiseUrl.searchParams.set("qty", qty);
    customiseButton.href = customiseUrl.toString();
  });
};

const changeLoyaltyPoints = points => {
  const loyaltyPointsEl = document.getElementById(
    "js-number-of-loyalty-points"
  );

  if (loyaltyPointsEl) {
    loyaltyPointsEl.innerText = points;
  }
};

// Stock items select input listener
on("change", "#js-listing-page-qty-picker", target => {
  const element = document.getElementById("js-listing-page-qty-input");
  if (element) {
    // Used for stock items
    element.value = target.value;
  }

  const selectedOption = target.options[target.selectedIndex];
  changePriceLabel(selectedOption.dataset);
  changeLoyaltyPoints(selectedOption.dataset.loyaltyPoints);
  changeKlarnaPrice(selectedOption.dataset);
});

const scrollToOption = (option, smooth = false) => {
  const div = option.closest(QTY_PICKERS);
  if (!div) return;
  const offsetY =
    option.offsetTop -
    div.offsetTop -
    div.offsetHeight / 2 +
    option.offsetHeight / 2;
  const offsetX =
    option.offsetLeft -
    div.offsetLeft -
    div.offsetWidth / 2 +
    option.offsetWidth / 2;

  div.scrollTo({
    top: offsetY,
    left: offsetX,
    behavior: smooth ? "smooth" : "auto"
  });
};

// Strip all selected from tiles when hidden without thickness-enabled
const stripHiddenSelections = () => {
  document.querySelectorAll(QTY_PICKERS).forEach(table => {
    const thick = table.classList.contains("thickness-enabled");
    const hiddenSelected = thick
      ? table.querySelectorAll(
          ".listing-page-qty-option:not(.thick-option).selected"
        )
      : table.querySelectorAll(
          ".listing-page-qty-option.thick-option.selected"
        );
    hiddenSelected.forEach(el =>
      el.classList.remove("selected", "inset-border-ink", "text-ink")
    );
  });
};

// Large quantity picker table options listener
on("click", ".js-listing-page-qty-option", target => {
  sessionStorage.setItem("selectedQty", target.dataset.qty);

  const selectedOptions = document.querySelectorAll(
    ".js-listing-page-qty-option.selected"
  );
  selectedOptions.forEach(option => {
    option.classList.remove("selected", "inset-border-ink", "text-ink");
    option.classList.add("text-ink-4");
  });

  target.classList.remove("text-ink-4");
  target.classList.add("selected", "inset-border-ink", "text-ink");

  const qtyLabel = document.querySelector(".js-selected-qty-label");
  if (qtyLabel) qtyLabel.textContent = target.dataset.qty;

  scrollToOption(target, true);
  changePriceLabel(target.dataset);
  changeKlarnaPrice(target.dataset);
  changeCustomiseUrl(target.dataset);
  changeLoyaltyPoints(target.dataset.loyaltyPoints);

  // This will replace the estimated-delivery with the correct value using turbo frame
  const { analyticsId } = target.dataset;
  const quantity = target.dataset.qty;

  const newSrc = `${window.countryCode}/shipping/calculator/pdp_estimate/${analyticsId}/${quantity}`;

  analytics.track("Quantity Changed", {
    analytics_id: analyticsId,
    location: "PDP",
    number: quantity
  });

  document.getElementById("estimated-delivery").src = newSrc;
  const compactFrame = document.getElementById("estimated-delivery-compact");
  if (compactFrame) compactFrame.src = newSrc;

  const event = new CustomEvent("changeBulkPricingQty", {
    detail: { qty: target.dataset.qty }
  });
  window.dispatchEvent(event);
});

window.setupPricingTable = () => {
  updateThickPriceOnVariantChange();

  const wasThickSelected = document.querySelector(
    "#thickness-options input[type='radio'][value='double']"
  )?.checked;

  const visiblePicker = window.matchMedia("(min-width: 992px)").matches
    ? document.querySelector(".js-large-quantity-picker-desktop")
    : document.querySelector(".js-large-quantity-picker-mobile");
  const preselected = visiblePicker?.querySelector(
    ".js-listing-page-qty-option.selected"
  );

  // If a quantity is already selected, respect it and just realign UI
  if (preselected) {
    stripHiddenSelections();
    scrollToOption(preselected);
    changeCustomiseUrl(preselected.dataset);
    changePriceLabel(preselected.dataset);
    changeLoyaltyPoints(preselected.dataset.loyaltyPoints);
  } else {
    // Otherwise get the qty saved in session storage
    const selectedQty = sessionStorage.getItem("selectedQty");
    const qtyOptions = wasThickSelected
      ? visiblePicker?.querySelectorAll(
          ".js-listing-page-qty-option.thick-option"
        )
      : visiblePicker?.querySelectorAll(
          ".js-listing-page-qty-option:not(.thick-option)"
        );
    const matchingQtyOption = [...(qtyOptions || [])].find(
      opt => opt.dataset.qty === selectedQty
    );

    if (selectedQty && matchingQtyOption) {
      matchingQtyOption.click();
    } else {
      sessionStorage.removeItem("selectedQty");
    }
  }

  const mobilePricing = document.querySelector(
    ".js-large-quantity-picker-mobile"
  );
  const pdpPrices = document.querySelectorAll(".pdp-price");

  if (mobilePricing) {
    mobilePricing.classList.remove("opacity-0");
    pdpPrices.forEach(el => el.classList.remove("opacity-0"));
  }

  stripHiddenSelections();
};

window.setupPricingTable();
